import { User, useAuth0 } from "@auth0/auth0-react";
import { ChatBubbleLeftRightIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { PaperClipIcon } from "@heroicons/react/24/solid";
import { useQueryClient } from "@tanstack/react-query";
import { ChatActions, ReceivedWebSocketMessage, WebSocketMessage, WebSocketServices } from "@utils/constants";
import { unwrapConnection } from "@utils/utils";
import clsx from "clsx";
import { format, isSameDay, isToday, isYesterday } from "date-fns";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaCircle } from "react-icons/fa6";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { useDebounce, useDebouncedCallback } from "use-debounce";

import { Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiChatStatusChoices,
  ChatMessageType,
  ChatType,
  useGetChatMessagesQuery,
  useUpdateChatMutation,
} from "@api/graphql/types-and-hooks";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { notifyError } from "@components/notifications/notifications";
import { ChatAttachment } from "@pages/FronteraClarity/components/ChatAttachment";
import { ChatWYSIWYG } from "@pages/FronteraClarity/components/ChatWYSIWYG";
import { LiveChatMessage } from "@pages/FronteraClarity/components/LiveChatMessage";
import { useChatFileUpload } from "@pages/FronteraClarity/components/useChatFileUpload";

interface LiveChatProps {
  chat: ChatType;
  /** Fired when the user taps a video thumbnail or an echo bubble's
   * subheader. Already resolves the "source" chat message (the one that
   * uploaded the video) into a pinned-note payload — the overlay renders
   * it as the "A note from …" attachment card above the comment list. */
  onVideoClick?: (params: {
    mediaId: string;
    pinnedNote?: {
      authorFirstName: string;
      authorLastName: string;
      content: string;
      createdDate: string;
    };
    /** Seek the player to this timestamp (seconds) on open. Only set
     *  when clicking an echo bubble subheader. */
    initialSeekTime?: number;
    /** Highlight this comment ID in the comment thread on open. */
    highlightCommentId?: string;
  }) => void;
}

// Pure helpers — no component state or props, safe to define outside the
// component so they don't sit between hook calls and trigger the React hooks
// linter's "called conditionally" false positive.
const formatDateLabel = (date: Date): string => {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMMM do, yyyy");
};

const isDifferentDay = (date1: Date, date2: Date): boolean => isSameDay(date1, date2) === false;

interface ChatWebSocketMessageContext {
  newMessage: string;
  messageCount: number;
  setNewMessage: (value: string) => void;
  setWysiwygKey: React.Dispatch<React.SetStateAction<number>>;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>;
  setIsOnline: (value: boolean) => void;
  setUsersTyping: React.Dispatch<React.SetStateAction<Set<string>>>;
  setLastMessageTimestamp: (value: number) => void;
  saveMessageDraft: (draft: string | null) => Promise<void>;
  cancelDraftSave: () => void;
  readLatestMessage: (message: ChatMessageType) => void;
  editSuccessCallbacksRef: React.MutableRefObject<Map<string, () => void>>;
  deleteSuccessCallbacksRef: React.MutableRefObject<Map<string, () => void>>;
}

// Handles a single WebSocketServices.CHAT message. Pulled out of the
// component so this switch is scored on its own for cognitive complexity
// instead of folding into LiveChat's.
const applyChatWebSocketMessage = (wsMessage: ReceivedWebSocketMessage, ctx: ChatWebSocketMessageContext): void => {
  switch (wsMessage.action) {
    case ChatActions.CONNECT:
      ctx.setIsOnline(true);
      return;
    case ChatActions.SEND: {
      const messagePayload = JSON.parse(wsMessage.payload.message as string);

      // clear the typed message and message draft once we know it succeeded
      if (messagePayload.content && messagePayload.content.trim() == ctx.newMessage.trim()) {
        ctx.cancelDraftSave();
        ctx.setNewMessage("");
        ctx.setWysiwygKey((k) => k + 1);
        ctx.saveMessageDraft(null);
      }

      const newMessageEdge: ChatMessageType = {
        id: messagePayload.id,
        user: messagePayload.user,
        content: messagePayload.content,
        role: messagePayload.role,
        sequenceNumber: messagePayload.sequenceNumber || ctx.messageCount + 1,
        createdDate: messagePayload.createdDate,
        lastUpdatedDate: messagePayload.lastUpdatedDate,
        chat: messagePayload.chat,
        organization: messagePayload.organization,
        readAt: messagePayload.readAt,
        readBy: messagePayload.readBy,
        userFirstName: messagePayload.userFirstName,
        userLastName: messagePayload.userLastName,
        file: messagePayload.file,
        media: messagePayload.media ?? null,
        mediaComment: messagePayload.mediaComment ?? null,
        edited: messagePayload.edited,
        editHistory: messagePayload.editHistory,
        archived: messagePayload.archived,
      };

      ctx.setMessages((prev) => [...prev, newMessageEdge]);
      ctx.readLatestMessage(newMessageEdge);

      // update timestamp to trigger debounced invalidation
      ctx.setLastMessageTimestamp(Date.now());
      return;
    }
    case ChatActions.DISCONNECT:
      ctx.setIsOnline(false);
      return;
    case ChatActions.TYPING: {
      const typingPayload = JSON.parse(wsMessage.payload.message as string);

      const isTyping = typingPayload.typing;
      const user = typingPayload.user as User;
      const chatId = typingPayload.chatId;

      if (!user.name) {
        return;
      }

      // track the state of which users are typing
      ctx.setUsersTyping((prev) => {
        const newSet = new Set(prev);
        if (isTyping) {
          newSet.add(`${chatId}:${user.name as string}`);
        } else {
          newSet.delete(`${chatId}:${user.name as string}`);
        }
        return newSet;
      });
      return;
    }
    // READ and EDIT are split so each case owns the exact set of
    // fields it mutates. Replacing the whole message would wipe the
    // newer media fields (thumbnailUrl, commentsCount, mediaComment)
    // because the websocket's markRead / updateChatMessage payload
    // fragments don't select them.
    case ChatActions.READ: {
      const readPayload = JSON.parse(wsMessage.payload.message as string);
      ctx.setMessages((prev) =>
        prev.map((message) =>
          message.id === readPayload.id
            ? { ...message, readAt: readPayload.readAt, readBy: readPayload.readBy }
            : message,
        ),
      );
      return;
    }
    case ChatActions.EDIT: {
      const editPayload = JSON.parse(wsMessage.payload.message as string);
      ctx.setMessages((prev) =>
        prev.map((message) =>
          message.id === editPayload.id
            ? {
                ...message,
                content: editPayload.content,
                edited: editPayload.edited,
                editHistory: editPayload.editHistory,
                lastUpdatedDate: editPayload.lastUpdatedDate,
              }
            : message,
        ),
      );
      ctx.editSuccessCallbacksRef.current.get(editPayload.id)?.();
      ctx.editSuccessCallbacksRef.current.delete(editPayload.id);
      return;
    }
    case ChatActions.DELETE: {
      const deletePayload = JSON.parse(wsMessage.payload.message as string);
      const deletedMessageId: string = deletePayload.id;

      // find the matching message id and update the state
      ctx.setMessages((prev) => prev.filter((message) => message.id !== deletedMessageId));

      ctx.deleteSuccessCallbacksRef.current.get(deletedMessageId)?.();
      ctx.deleteSuccessCallbacksRef.current.delete(deletedMessageId);
      return;
    }
  }
};

export const LiveChat: React.FC<LiveChatProps> = ({ chat, onVideoClick }) => {
  // set access token
  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const [accessToken, setAccessToken] = useState<string>();

  // TODO: using invalidateQueries should be temporary and we should rebuild the chat UI around the WS updating the chats state directly to avoid a bunch of refetches
  const queryClient = useQueryClient();
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState<number>(0);
  const [debouncedTimestamp] = useDebounce(lastMessageTimestamp, 3000);

  // File upload functionality
  const { attachment, setAttachmentFile, removeAttachment, clearAttachment } = useChatFileUpload(chat.learner.id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // edit delete message refs
  const editSuccessCallbacksRef = useRef<Map<string, () => void>>(new Map());
  const deleteSuccessCallbacksRef = useRef<Map<string, () => void>>(new Map());

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAttachmentFile(file);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (debouncedTimestamp > 0) {
      // invalidate queries when debounced timestamp changes
      queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
      // Keep the chat messages cache fresh after new messages settle.
      queryClient.invalidateQueries({ queryKey: ["getChatMessages", chat.id] });
      // Also invalidate the sidebar Multimedia gallery cache so a brand-
      // new chat-uploaded video or image attachment shows up without a
      // page reload. If the accordion is closed, the sidebar query is
      // `enabled: false` — invalidation just marks it stale and the
      // next expansion picks up the fresh data. If the accordion is
      // open, this triggers a background refetch.
      queryClient.invalidateQueries({ queryKey: ["getChatMedia", chat.id] });
    }
  }, [debouncedTimestamp, queryClient, chat.id]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (mounted) {
          setAccessToken(token);
        }
      } catch (error) {
        console.error("Failed to get access token:", error);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently]);

  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [newMessage, setNewMessage] = useState<string>(chat.messageDraft || "");
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const updateChatMutation = useUpdateChatMutation();

  const saveMessageDraft = useCallback(
    async (draft: string | null) => {
      try {
        const response = await updateChatMutation.mutateAsync({
          input: { chatId: chat.id, messageDraft: draft },
        });

        if (!response.updateChat?.success) {
          notifyError(response.updateChat?.message || "Failed to update message draft");
          return;
        }
      } catch (error) {
        console.error("Failed to update message draft:", error);
        notifyError("Failed to update message draft");
      }
    },
    [chat.id, updateChatMutation.mutateAsync],
  );

  const debouncedSaveDraft = useDebouncedCallback((value: string) => {
    const draft = value.trim() || null;
    saveMessageDraft(draft);
  }, 500);

  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [usersTyping, setUsersTyping] = useState<Set<string>>(new Set());

  // connect to websocket
  const socketUrl = accessToken ? import.meta.env.VITE_WEBSOCKET_DOMAIN : null;

  const { sendJsonMessage, readyState } = useWebSocket<ReceivedWebSocketMessage>(socketUrl, {
    share: true,
    onClose: () => {
      setIsOnline(false);
    },
    onError: (error) => {
      console.error("WebSocket error:", error);
    },
    onMessage: (event) => {
      if (!accessToken) {
        return;
      }

      const wsMessage = JSON.parse(event.data) as ReceivedWebSocketMessage;

      if (wsMessage.payload.status !== 200) {
        notifyError(wsMessage.payload.message as string);
        return;
      }

      if (wsMessage.service === WebSocketServices.CHAT) {
        applyChatWebSocketMessage(wsMessage, {
          newMessage,
          messageCount: messages.length,
          setNewMessage,
          setWysiwygKey,
          setMessages,
          setIsOnline,
          setUsersTyping,
          setLastMessageTimestamp,
          saveMessageDraft,
          cancelDraftSave: debouncedSaveDraft.cancel,
          readLatestMessage,
          editSuccessCallbacksRef,
          deleteSuccessCallbacksRef,
        });
      }
    },
    shouldReconnect: () => !!accessToken,
  });

  const readLatestMessage = (latestMessage: ChatMessageType) => {
    // skip marking latest message as read if current user sent the message
    if (latestMessage?.user?.username == auth0User?.sub) {
      return;
    }

    const readPayload: WebSocketMessage = {
      service: WebSocketServices.CHAT,
      action: ChatActions.READ,
      timestamp: new Date(),
      accessToken: accessToken || "",
      payload: {
        chatId: chat.id,
        messageId: latestMessage.id,
        read: true,
      },
    };
    sendJsonMessage(readPayload);
  };

  // connect/disconnect when chat changes
  useEffect(() => {
    if (!accessToken || readyState !== ReadyState.OPEN) {
      return;
    }

    // Re-fetch messages on (re)connect so the list is never stale after a
    // WebSocket drop. Previously this was implicit because readyState was
    // part of the query key; now we trigger it explicitly.
    queryClient.invalidateQueries({ queryKey: ["getChatMessages", chat.id] });
    // Same for the sidebar Multimedia gallery — invalidate here too so
    // attachments uploaded during the disconnect surface after reconnect.
    // The sidebar query is gated on `openChatMedia`, so this only causes
    // a network call when the accordion is currently open.
    queryClient.invalidateQueries({ queryKey: ["getChatMedia", chat.id] });

    // this will trigger the interval to begin
    setIsOnline(false);
    setUsersTyping(new Set());
    setNewMessage(chat.messageDraft || "");
    removeAttachment();
    debouncedSaveDraft.cancel();

    // disconnect when chatId changes or component unmounts
    return () => {
      debouncedSaveDraft.cancel();
      const disconnectPayload: WebSocketMessage = {
        service: WebSocketServices.CHAT,
        action: ChatActions.DISCONNECT,
        timestamp: new Date(),
        accessToken: accessToken,
        payload: {
          chatId: chat.id, // this will be the old chatId when dependencies change
        },
      };
      sendJsonMessage(disconnectPayload);
      setIsOnline(false);
      setUsersTyping(new Set());
      setNewMessage("");
    };
  }, [chat.id, accessToken, readyState, sendJsonMessage, queryClient]);

  // retry connecting on an interval when offline
  useEffect(() => {
    // only retry if we're offline, have access token, and WebSocket is open
    if (isOnline || !accessToken || readyState !== ReadyState.OPEN) {
      return;
    }

    // use interval to retry connection every second
    const retryInterval = setInterval(() => {
      if (!accessToken || readyState !== ReadyState.OPEN) {
        return;
      }

      const connectPayload: WebSocketMessage = {
        service: WebSocketServices.CHAT,
        action: ChatActions.CONNECT,
        timestamp: new Date(),
        accessToken: accessToken,
        payload: {
          chatId: chat.id,
        },
      };
      sendJsonMessage(connectPayload);
    }, 1000);

    // clear interval when online or component unmounts
    return () => {
      clearInterval(retryInterval);
    };
  }, [isOnline, accessToken, readyState, chat.id, sendJsonMessage]);

  // get chat messages
  const {
    data: chatMessageData,
    isLoading,
    error,
  } = useGetChatMessagesQuery({ chatId: chat.id }, { queryKey: ["getChatMessages", chat.id] });

  // Notify once when the query errors — must be a useEffect so this side
  // effect doesn't run during render and so hooks below are never skipped.
  useEffect(() => {
    if (error) {
      notifyError("Failed to load chat messages!");
    }
  }, [error]);

  // set messages on rerenders/refetches
  useEffect(() => {
    const chatMessages = unwrapConnection(chatMessageData?.chatMessages) || [];
    setMessages(chatMessages as ChatMessageType[]);
  }, [chatMessageData]);

  // read latest message when coming online
  useEffect(() => {
    if (!isOnline) {
      return;
    }

    const lastMessage = (messages.at(-1) as ChatMessageType) || chat.lastMessage;
    const lastMessageRead = lastMessage ? lastMessage.readAt : null;

    if (!lastMessage || lastMessageRead) {
      return;
    }

    readLatestMessage(lastMessage);

    // refetch chats for updated state
    if (chat?.status !== ApiChatStatusChoices.Finished) {
      queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
  }, [isOnline, queryClient]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // for typing tracking
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [wysiwygKey, setWysiwygKey] = useState<number>(0);

  // remount WYSIWYG when online state changes so placeholder updates
  useEffect(() => {
    setWysiwygKey((k) => k + 1);
  }, [isOnline]);

  const [typedMessage] = useDebounce(newMessage, 1000);

  useEffect(() => {
    if (!accessToken || !isOnline) {
      return;
    }

    const messagePayload: WebSocketMessage = {
      service: WebSocketServices.CHAT,
      action: ChatActions.TYPING,
      timestamp: new Date(),
      accessToken: accessToken,
      payload: { chatId: chat.id, typing: isTyping },
    };
    sendJsonMessage(messagePayload);
  }, [isTyping]);

  // stop typing indicator and save message draft when debounced input changes
  useEffect(() => {
    setIsTyping(false);
  }, [typedMessage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  // Re-scroll when the messages container shrinks (e.g. WYSIWYG renders and takes up space)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => scrollToBottom());
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const isReady = readyState === ReadyState.OPEN;

  const sendMessage = async () => {
    if (!isReady) {
      notifyError("You must be connected to send messages.");
      return;
    }

    if (!accessToken) {
      notifyError("You must be authenticated to send messages. Try refreshing.");
      return;
    }

    // Check if attachment is uploaded and ready
    let fileId: string | undefined = undefined;
    if (attachment) {
      // Wait for upload to complete if still uploading
      if (attachment.status === "uploading") {
        notifyError("Please wait for the file to finish uploading.");
        return;
      }
      // Check if upload failed
      if (attachment.status === "error") {
        notifyError("File upload failed. Please try again.");
        return;
      }
      // Use the fileId from the uploaded attachment
      if (attachment.status === "uploaded" && attachment.fileId) {
        fileId = attachment.fileId;
      }
    }

    // message recipient should be the caregivers auth0 ID
    const recipients =
      chat.learner.relationshipSet?.edges
        ?.map((edge) => ({
          auth0Id: edge?.node?.caregiver.auth0User?.auth0id,
          orgId: edge?.node?.organization.id,
        }))
        .filter((recipient) => recipient.auth0Id != null) || [];

    const messagePayload: WebSocketMessage = {
      service: WebSocketServices.CHAT,
      action: ChatActions.SEND,
      timestamp: new Date(),
      accessToken: accessToken,
      payload: {
        chatId: chat.id,
        message: newMessage,
        recipients: recipients,
        fileId: fileId,
      },
    };
    sendJsonMessage(messagePayload);

    setIsTyping(false);
    clearAttachment();
  };

  const handleDelete = (deleteMessageId: string, onSuccess: () => void) => {
    if (!isReady) {
      notifyError("You must be connected to delete messages.");
      return;
    }

    if (!accessToken) {
      notifyError("You must be authenticated to delete messages. Try refreshing.");
      return;
    }

    deleteSuccessCallbacksRef.current.set(deleteMessageId, onSuccess);

    const messagePayload: WebSocketMessage = {
      service: WebSocketServices.CHAT,
      action: ChatActions.DELETE,
      timestamp: new Date(),
      accessToken: accessToken,
      payload: {
        chatId: chat.id,
        deleteMessageId: deleteMessageId,
      },
    };
    sendJsonMessage(messagePayload);
  };

  const handleEdit = (chatMessageId: string, editedMessage: string, onSuccess: () => void) => {
    if (!isReady) {
      notifyError("You must be connected to edit messages.");
      return;
    }

    if (!accessToken) {
      notifyError("You must be authenticated to edit messages. Try refreshing.");
      return;
    }

    editSuccessCallbacksRef.current.set(chatMessageId, onSuccess);

    const messagePayload: WebSocketMessage = {
      service: WebSocketServices.CHAT,
      action: ChatActions.EDIT,
      timestamp: new Date(),
      accessToken: accessToken,
      payload: {
        chatId: chat.id,
        editedMessage: {
          id: chatMessageId,
          content: editedMessage,
        },
      },
    };
    sendJsonMessage(messagePayload);
  };

  // Resolve the "source upload" chat message — the one that uploaded the
  // clicked video — and bubble it up as a pinned-note payload so the
  // overlay can render it as the "A note from …" attachment card. Works
  // for both thumbnail clicks (clicked message IS the source) and echo
  // bubble clicks (a different message owns the source media).
  const handleInnerVideoClick = useCallback(
    (mediaId: string, seekTime?: number, commentId?: string) => {
      if (!onVideoClick) return;
      const source = messages.find((m) => m.media?.id === mediaId);
      onVideoClick({
        mediaId,
        pinnedNote: source
          ? {
              authorFirstName: source.userFirstName ?? "",
              authorLastName: source.userLastName ?? "",
              content: source.content ?? "",
              createdDate: source.createdDate,
            }
          : undefined,
        initialSeekTime: seekTime,
        highlightCommentId: commentId,
      });
    },
    [messages, onVideoClick],
  );

  if (error) return null;

  const renderMessagesContent = (): React.ReactNode => {
    if (isLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4" />
          <Small className="text-text-secondary">Loading messages...</Small>
        </div>
      );
    }
    if (messages.length === 0) {
      return (
        <div className="text-center py-8">
          <ChatBubbleLeftRightIcon className="h-12 w-12 text-limestone-300 mx-auto mb-4" />
          <Small className="text-text-secondary">No messages in this chat yet.</Small>
        </div>
      );
    }
    return (
      <div className="flex flex-col w-full">
        {messages.map((message, index) => {
          if (!message) return null;

          const currentDate = new Date(message.createdDate);
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const previousDate = previousMessage?.createdDate ? new Date(previousMessage.createdDate) : null;

          const showDateDivider = !previousDate || isDifferentDay(currentDate, previousDate);

          const isLastMessage = index === messages.length - 1;
          return (
            <div key={message.id}>
              {showDateDivider && (
                <div className="flex items-center justify-center my-6">
                  <div className="flex items-center gap-2 w-full">
                    <div className="flex-1 border-t border-limestone-200"></div>
                    <Small className="px-4 whitespace-nowrap">{formatDateLabel(currentDate)}</Small>
                    <div className="flex-1 border-t border-limestone-200"></div>
                  </div>
                </div>
              )}
              <LiveChatMessage
                message={message}
                isLastMessage={isLastMessage}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                onVideoClick={handleInnerVideoClick}
              />
            </div>
          );
        })}

        {/* Users typing */}
        {Array.from(usersTyping).map((typingStr) => {
          const splitObj = typingStr.split(":");

          const typingChatId = splitObj[0];
          const splitName = splitObj[1].split(" ");
          const firstName = splitName[0];
          const lastName = splitName[1];

          if (typingChatId != chat.id) {
            return;
          }

          return (
            <div key={typingStr} className="flex flex-row items-center gap-2 pb-4">
              <UserInitialsView
                className="h-8! w-8! min-h-8! min-w-8! text-[16px]! leading-4"
                firstName={firstName}
                lastName={lastName}
              />
              <div className="flex flex-row items-center gap-1 p-4 w-16 h-8 border rounded-xl border-limestone-200 bg-limestone-50">
                <FaCircle className="w-2 h-2 text-limestone-300" />
                <FaCircle className="w-2 h-2 text-limestone-300" />
                <FaCircle className="w-2 h-2 text-limestone-300" />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getWysiwygTitle = (): string => {
    if (!newMessage || !isOnline) return "";
    return isTyping ? "Saving draft..." : "Draft Saved";
  };

  return (
    <div className="flex flex-col px-4 pb-4 w-full h-full min-h-0 justify-between bg-white border-b border-x rounded-b-md">
      <div className="messages-container overflow-y-auto no-scrollbar min-h-0 flex-1" ref={messagesContainerRef}>
        {renderMessagesContent()}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-row">
          <ChatWYSIWYG
            key={`${chat.id}-${wysiwygKey}`}
            title={getWysiwygTitle()}
            placeholder={isOnline ? "Type a Message" : "Frontera is trying to connect..."}
            existingContent={isOnline ? newMessage : ""}
            onSave={async ({ content }) => {
              setNewMessage(content);
              setIsTyping(content.length > 0);
              debouncedSaveDraft(content);
            }}
            isSaving={false}
            autoSave={true}
            autoSaveDebounce={0} // setting this to 0 basically creates an onChange effect
            displayOnlyMode={!isOnline}
            editMode={true}
            showTitle={true}
            showAutoSaveMessage={false}
            readOnlyTimerDelay={2147483647} // setting this to a large nmber keeps the tool bar open at all times
            containerClassName="mt-0! mb-0! border-0"
            inputContainerClassName={clsx("border bg-white", !newMessage && "mt-6")}
          />

          {/* Attachment + send row fixed at bottom */}
          {isOnline && (
            <div className="flex flex-row items-end justify-between px-4 pb-2 pt-2">
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept="image/*,.pdf,.doc,.docx,.txt"
                />
                <PaperClipIcon
                  className={`h-5 w-5 ${isReady && !attachment ? "text-text-secondary cursor-pointer hover:text-limestone-700" : "text-limestone-300 cursor-not-allowed"}`}
                  onClick={isReady && !attachment ? () => fileInputRef.current?.click() : undefined}
                />
                <PaperAirplaneIcon
                  className={`h-5 w-5 ${isReady ? "text-text-secondary cursor-pointer" : "text-limestone-300 cursor-not-allowed"}`}
                  onClick={isReady ? sendMessage : undefined}
                />
              </div>
            </div>
          )}
        </div>

        {attachment && (
          <div className="flex items-center min-w-0">
            <ChatAttachment
              fileName={attachment.file.name}
              fileSize={attachment.file.size}
              mimeType={attachment.file.type}
              variant="compose"
              status={attachment.status}
              onRemove={removeAttachment}
            />
          </div>
        )}
      </div>
    </div>
  );
};
